import {
  differenceInCalendarDays,
  differenceInMinutes,
  endOfMonth,
  endOfQuarter,
  format,
  isSameYear,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
} from "date-fns";

import { Export } from "~/api/Api";

/** A closed range of calendar days, both ends at local midnight. */
export type ReportPeriod = { from: Date; to: Date };

// Mirrors ClientReport::MAX_DAYS on the backend.
export const MAX_PERIOD_DAYS = 366;

// Mirrors Export::RECENT_TIMEFRAME: the backend refuses a second report
// for the same period while an earlier one is this recent.
const RECENT_TIMEFRAME_MINUTES = 60;

const RECENT_STATUSES: Export["status"][] = [
  "created",
  "processing",
  "completed",
];

export const periodDays = (period: ReportPeriod) =>
  differenceInCalendarDays(period.to, period.from) + 1;

/** The period of the same length right before this one, which the report compares against. */
export const previousPeriod = (period: ReportPeriod): ReportPeriod => {
  const days = periodDays(period);

  return { from: subDays(period.from, days), to: subDays(period.from, 1) };
};

/** "Sep 1 – Sep 30, 2026", the same label the PDF uses. */
export const formatPeriod = (period: ReportPeriod) => {
  const fromFormat = isSameYear(period.from, period.to)
    ? "MMM d"
    : "MMM d, yyyy";

  return `${format(period.from, fromFormat)} – ${format(period.to, "MMM d, yyyy")}`;
};

export const toApiPeriod = (period: ReportPeriod) => ({
  starts_on: format(period.from, "yyyy-MM-dd"),
  ends_on: format(period.to, "yyyy-MM-dd"),
});

// parseISO reads a bare date as local midnight; `new Date("2026-09-01")`
// would read it as UTC and shift the day west of Greenwich.
export const fromApiPeriod = (
  startsOn: string | null,
  endsOn: string | null,
): ReportPeriod | null =>
  startsOn && endsOn
    ? { from: parseISO(startsOn), to: parseISO(endsOn) }
    : null;

/** The backend's validation, as a message for the first rule broken. */
export const validatePeriod = (
  period: ReportPeriod,
  today: Date = new Date(),
): string | null => {
  if (period.from > period.to) {
    return "The period has to start before it ends.";
  }

  if (startOfDay(period.to) > startOfDay(today)) {
    return "The period can't end in the future.";
  }

  if (periodDays(period) > MAX_PERIOD_DAYS) {
    return `A report can cover at most ${MAX_PERIOD_DAYS} days.`;
  }

  return null;
};

export const findRecentReportForPeriod = (
  exports: Export[],
  period: ReportPeriod,
  now: Date = new Date(),
): Export | undefined => {
  const { starts_on, ends_on } = toApiPeriod(period);

  return exports.find(
    (report) =>
      report.type === "ClientReport" &&
      report.starts_on === starts_on &&
      report.ends_on === ends_on &&
      RECENT_STATUSES.includes(report.status) &&
      differenceInMinutes(now, parseISO(report.created_at)) <
        RECENT_TIMEFRAME_MINUTES,
  );
};

// Presets end yesterday at the latest: today's data is still coming in.
export const PERIOD_PRESETS: {
  label: string;
  value: (today: Date) => ReportPeriod;
}[] = [
  {
    label: "Last month",
    value: (today) => {
      const month = subMonths(startOfMonth(today), 1);

      return { from: month, to: startOfDay(endOfMonth(month)) };
    },
  },
  {
    label: "Last 30 days",
    value: (today) => {
      const yesterday = subDays(startOfDay(today), 1);

      return { from: subDays(yesterday, 29), to: yesterday };
    },
  },
  {
    label: "Last quarter",
    value: (today) => {
      const quarter = subQuarters(startOfQuarter(today), 1);

      return { from: quarter, to: startOfDay(endOfQuarter(quarter)) };
    },
  },
  {
    label: "Last 90 days",
    value: (today) => {
      const yesterday = subDays(startOfDay(today), 1);

      return { from: subDays(yesterday, 89), to: yesterday };
    },
  },
  {
    label: "Year to date",
    value: (today) => {
      const yesterday = subDays(startOfDay(today), 1);

      return { from: startOfYear(yesterday), to: yesterday };
    },
  },
];

export const isSamePeriod = (a: ReportPeriod, b: ReportPeriod) =>
  a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime();
